import { useState, useEffect } from 'react';
import API from '../services/api';
import { X, Upload, Save } from 'lucide-react';

function InvoiceSettings({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    owner_name: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    tax_number: '',
    vat_id: '',
    email: '',
    phone: '',
    bank_name: '',
    iban: '',
    bic: '',
    payment_terms: '14 Tage',
    footer_note: '',
    logo_url: '',
    invoice_counter: 1,
    invoice_prefix: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    try {
      const { data } = await API.get('/invoicing/settings');
      setSettings(data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await API.put('/invoicing/settings', settings);
      alert('Einstellungen gespeichert!');
      onClose();
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Fehler beim Speichern!');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Für jetzt: Base64 (später: echtes File-Upload)
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await API.post('/invoicing/settings/logo', { logo_url: reader.result });
        setSettings({ ...settings, logo_url: reader.result });
        alert('Logo hochgeladen!');
      } catch (err) {
        console.error('Error uploading logo:', err);
      }
    };
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--bg)',
        borderRadius: 12,
        width: '90%',
        maxWidth: 800,
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: 20,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ margin: 0 }}>Rechnungseinstellungen</h2>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: 8 }}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: 4,
          padding: '12px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg2)'
        }}>
          {[
            { id: 'company', label: 'Firmendaten' },
            { id: 'logo', label: 'Logo & Layout' },
            { id: 'format', label: 'Rechnungsformat' },
            { id: 'numbering', label: 'Nummerierung' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="btn"
              style={{
                background: activeTab === tab.id ? 'var(--accent)' : 'transparent',
                color: activeTab === tab.id ? 'white' : 'var(--text2)',
                border: 'none',
                padding: '8px 16px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {/* TAB 1: FIRMENDATEN */}
          {activeTab === 'company' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Firmenname
                </label>
                <input
                  type="text"
                  className="input"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="z.B. Mustermann GmbH"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Inhaber / Geschäftsführer
                </label>
                <input
                  type="text"
                  className="input"
                  value={settings.owner_name}
                  onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })}
                  placeholder="z.B. Max Mustermann"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                    Straße & Hausnummer
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={settings.address}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                    placeholder="Musterstraße 123"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>PLZ</label>
                  <input
                    type="text"
                    className="input"
                    value={settings.postal_code}
                    onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
                    placeholder="12345"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Stadt</label>
                  <input
                    type="text"
                    className="input"
                    value={settings.city}
                    onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                    placeholder="Berlin"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                    Steuernummer
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={settings.tax_number}
                    onChange={(e) => setSettings({ ...settings, tax_number: e.target.value })}
                    placeholder="12/345/67890"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                    USt-IdNr.
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={settings.vat_id}
                    onChange={(e) => setSettings({ ...settings, vat_id: e.target.value })}
                    placeholder="DE123456789"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>E-Mail</label>
                  <input
                    type="email"
                    className="input"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    placeholder="rechnung@firma.de"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Telefon</label>
                  <input
                    type="text"
                    className="input"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <h3 style={{ fontSize: 16, marginBottom: 12 }}>Bankverbindung</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Bank</label>
                    <input
                      type="text"
                      className="input"
                      value={settings.bank_name}
                      onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                      placeholder="Sparkasse"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>IBAN</label>
                      <input
                        type="text"
                        className="input"
                        value={settings.iban}
                        onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                        placeholder="DE89 3704 0044 0532 0130 00"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>BIC</label>
                      <input
                        type="text"
                        className="input"
                        value={settings.bic}
                        onChange={(e) => setSettings({ ...settings, bic: e.target.value })}
                        placeholder="COBADEFFXXX"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: LOGO & LAYOUT */}
          {activeTab === 'logo' && (
            <div style={{ display: 'grid', gap: 24 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                  Firmenlogo
                </label>
                <div style={{
                  border: '2px dashed var(--border)',
                  borderRadius: 8,
                  padding: 32,
                  textAlign: 'center',
                  background: 'var(--bg2)'
                }}>
                  {settings.logo_url ? (
                    <div>
                      <img
                        src={settings.logo_url}
                        alt="Logo"
                        style={{ maxWidth: 200, maxHeight: 100, marginBottom: 16 }}
                      />
                      <br />
                      <label className="btn">
                        <Upload size={16} style={{ marginRight: 6 }} />
                        Neues Logo hochladen
                        <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                      </label>
                    </div>
                  ) : (
                    <label className="btn">
                      <Upload size={16} style={{ marginRight: 6 }} />
                      Logo hochladen
                      <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                  Rechnungslayout (in Entwicklung)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {['Klassisch', 'Modern', 'Minimal'].map(layout => (
                    <div
                      key={layout}
                      style={{
                        border: '2px solid var(--border)',
                        borderRadius: 8,
                        padding: 16,
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'var(--bg2)'
                      }}
                    >
                      <div style={{
                        height: 120,
                        background: 'var(--bg)',
                        borderRadius: 4,
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text3)'
                      }}>
                        Vorschau
                      </div>
                      <div style={{ fontWeight: 500 }}>{layout}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: RECHNUNGSFORMAT */}
          {activeTab === 'format' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Zahlungsbedingungen
                </label>
                <input
                  type="text"
                  className="input"
                  value={settings.payment_terms}
                  onChange={(e) => setSettings({ ...settings, payment_terms: e.target.value })}
                  placeholder="14 Tage"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Fußnote (optional)
                </label>
                <textarea
                  className="input"
                  value={settings.footer_note}
                  onChange={(e) => setSettings({ ...settings, footer_note: e.target.value })}
                  placeholder="Vielen Dank für Ihre Zusammenarbeit!"
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                  Auf Rechnung anzeigen:
                </label>
                <div style={{ display: 'grid', gap: 8 }}>
                  {[
                    'Bestellnummern einzeln auflisten',
                    'Transaktionsnummern anzeigen',
                    'Agent-Name anzeigen',
                    'Produktnamen auflisten'
                  ].map(option => (
                    <label key={option} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: NUMMERIERUNG */}
          {activeTab === 'numbering' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Nächste Rechnungsnummer
                </label>
                <input
                  type="number"
                  className="input"
                  value={settings.invoice_counter}
                  onChange={(e) => setSettings({ ...settings, invoice_counter: parseInt(e.target.value) })}
                />
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                  Die nächste Rechnung erhält diese Nummer
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
                  Präfix (optional)
                </label>
                <input
                  type="text"
                  className="input"
                  value={settings.invoice_prefix}
                  onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
                  placeholder="RE-"
                />
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                  Wird vor die Nummer gesetzt, z.B. "RE-20250267"
                </div>
              </div>

              <div style={{
                padding: 16,
                background: 'var(--bg2)',
                borderRadius: 8,
                border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Vorschau:</div>
                <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>
                  {settings.invoice_prefix}{settings.invoice_counter}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: 20,
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12
        }}>
          <button onClick={onClose} className="btn btn-ghost">
            Abbrechen
          </button>
          <button onClick={handleSave} className="btn" disabled={loading} style={{
            background: 'var(--accent)',
            color: 'white'
          }}>
            <Save size={16} style={{ marginRight: 6 }} />
            {loading ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InvoiceSettings;
